/* global BUILD_TIMESTAMP */ // L4

// https://github.com/getsentry/sentry/blob/42eda359bf9b18a02ff5dccea9763d846cda780c/src/sentry/static/sentry/app/utils/theme.tsx

// dependencies
import { camelCase, get } from "lodash-es";
import { ThemeProvider } from "styled-components";
import { wpDesignTokens } from "@pazien/component-library";

// other
import { bodyFontSize, colorLegacy, colorStyle2pt0, headerFontSize } from "common/themes/designTokens/designTokens";
import { store } from "common/store";

const isStyle2pt0 = store?.getState?.()?.settings?.isStyle2pt0; // L1

const spaces = {
    "0": "0",
    "0.25": "2px",
    "0.5": "4px",
    "0.75": "6px",
    "1": "8px",
    "1.5": "12px",
    "2": "16px",
    "3": "20px",
    "4": "30px"
};

const fontSizes = {
    "0": "36px", // font-size: ${({ theme }) => theme.headerFontSize.large.value};
    "1": "27px", // font-size: ${({ theme }) => theme.headerFontSize.medium.value};
    "2": "20px", // font-size: ${({ theme }) => theme.headerFontSize.regular.value};
    "3": "16px", // font-size: ${({ theme }) => theme.bodyFontSize.extra_large.value};
    "4": "14px", // font-size: ${({ theme }) => theme.bodyFontSize.large.value};
    "5": "12px", // font-size: ${({ theme }) => theme.bodyFontSize.medium.value};
    "6": "10px", // font-size: ${({ theme }) => theme.bodyFontSize.small.value};
    "7": "8px", // font-size: ${({ theme }) => theme.bodyFontSize.extra_small.value};
    get h0() { return this["0"]; },
    get h1() { return this["1"]; },
    get h2() { return this["2"]; },
    get h3() { return this["3"]; },
    get h4() { return this["4"]; },
    get h5() { return this["5"]; },
    get h6() { return this["6"]; },
    get h7() { return this["7"]; },
    get h8() { return this["8"]; }
};

const fontStyles = {
    regular: "\"nunito_sans_regular\", sans-serif", // font-family: ${({ theme }) => theme.text.regular};
    medium: "\"nunito_sans_semibold\", sans-serif", // font-family: ${({ theme }) => theme.text.medium};
    bold: "\"nunito_sans_bold\", sans-serif", // font-family: ${({ theme }) => theme.text.bold};
    light: "\"nunito_sans_light\", sans-serif" // font-family: ${({ theme }) => theme.text.light};
};

const {
    blue3,
    blue6,
    blue11,
    brick1,
    canvas1,
    canvas4,
    gray1,
    gray4,
    gray6,
    green1,
    green4,
    keylime1,
    orange3,
    purple3,
    wcag5,
    wcag6
} = colorLegacy;

const analyticsSectionColors = {
    all_fraud: brick1.value,
    authorizations: blue3.value,
    averageTicket: orange3.value,
    cashflow: green4.value,
    chargebacks: purple3.value,
    fees: orange3.value,
    fraudsight: green1.value,
    misc: keylime1.value,
    netSettledCash: blue6.value,
    psd2_fraud: brick1.value,
    refundRate: blue6.value,
    refunds: blue6.value,
    sales: wcag6.value,
    settlements: wcag6.value,
    threedsflex: green1.value
};

const ellipsis = (maxWidth = "100%", display = "inline-block") => `
    display: ${display};
    max-width: ${maxWidth};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ellipsis2 = (maxWidth = "100%") => `
    max-width: ${maxWidth};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// From base.scss, to be main theme colors
const baseStyles = {
    // Text colors
    textColorPrimary: `${gray1.value} !default`,
    textColorPrimaryKnockout: `${canvas4.value} !default`,
    textColorSecondary: `${gray4.value} !default`,

    // Background colors
    backgroundColorPrimary: `${canvas4.value} !default`,
    backgroundColorSecondary: `${canvas1.value} !default`,

    // Branding colors
    colorCorpPrimary: `${wcag5.value} !default`,
    colorCorpSecondary: `${green4.value} !default`,

    // Misc colors
    colorActiveLink: `${green4.value} !default`,

    // Dimensions
    "dimension-topbar-border-width": "1px !default",
    "dimension-topbar-height": "50px !default",
    "page-padding": "16px !default",
    "popover-max-width": "500px !default",
    "sidebar-padding-large": "180px !default",
    "sidebar-padding-small-plus-scroll": "70px !default",
    "sidebar-padding-small": "50px !default",
    "sidebar-small-width": "50px !default",
    "sidebar-width": "180px !default",
    "sidebar-zindex": "1028 !default",
    "tab-bar-height": "39px !default",
    "top-content-bar-height": "62px !default"
};

/**
 * Gets a legacy design token which is guaranteed not to be `undefined`
 *   or `null`. This keeps the application from crashing when the toggle
 *   for `isStyle2pt0` is not enabled but the code is looking for a
 *   style 2.0 design token.
 * @param {string} colorName
 * @todo Once legacy design tokens are eliminated, this function and the
 *   lines that call it can be removed.
 * @returns {Object} The value of the design token.
 */
const getSubstitueColorValue = (colorName) => {
    if (colorName === "palette-border-quaternary") {
        return gray6;
    }
    if (colorName === "palette-brand-primary") {
        return blue11;
    }
    return colorStyle2pt0.generic;
};

/**
 * Gets the value of a single design token.
 * @param {string} type `color` is the only supported type for now
 * @param {string} tokenName for example, "blue3", "gray1"
 * @param {string} legacyValue a fallback hex code value to use if
 *   `tokenName` is not found at `theme.color[tokenName]` (e.g. #cccccc)
 * @todo We currently support only colors. Add support for other types
 *   when they become available.
 * @returns {string} The value of the design token.
 */
export const getDesignToken = (type, tokenName, legacyValue = "") => {
    if (type !== "color") {
        throw new Error(`Unsupported design token type: ${type}.`);
    }
    if (typeof tokenName !== "string") {
        throw new TypeError("The type for `tokenName` must be a string.");
    }
    if (!theme.color[tokenName] && legacyValue) {
        return legacyValue;
    } else if (!theme.color[tokenName] && !legacyValue) {
        return colorStyle2pt0.generic.value;
    }
    return theme.color[tokenName].value;
};

/**
 * Gets the values of multiple design tokens.
 * @param {string} type `color` is the only supported type for now
 * @param {string[]} tokenNames for example, `["white1", "black1"]`
 * @todo We currently support only colors. Add support for other types
 *   when they become available.
 * @returns {{[key: string]: string}} An object of key/value pairs where
 *   the key names are the design token names converted to camel case
 *   and the corresponding value for each one is the design token's
 *   value expressed as a string.
 */
export const getDesignTokens = (type, tokenNames) => {
    const isLocal = process.env.NODE_ENV === "development" || window.location.hostname === "localhost";
    if (isLocal && typeof window !== "undefined") {
        window.walog = window.walog || [];
    }
    if (type !== "color") {
        throw new Error(`Unsupported design token type: ${type}`);
    }
    if (typeof tokenNames !== "object") {
        throw new TypeError("The type for `tokenNames` must be an array.");
    }
    const result = tokenNames.reduce((acc, tokenName) => {
        const keyname = camelCase(tokenName);
        if (!theme.color[tokenName]) {
            acc[keyname] = getSubstitueColorValue(tokenName).value;
            // L5
            isLocal && window.walog.push(`PAZIEN ERROR for getDesignTokens: ${tokenName} as a color is not currently supported. A substitute color is being used instead.`);
        } else {
            acc[keyname] = theme.color[tokenName].value;
        }
        return acc;
    }, {});
    return { ...result };
};

const waTheme = { // L2
    analyticsSectionColor: (section) => get(analyticsSectionColors, section),
    backgroundColor: baseStyles.backgroundColorPrimary,
    baseStyles,
    bodyFontSize,
    borderRadius: "2px",
    color: colorLegacy,
    colorCorp: baseStyles.colorCorpPrimary,
    font: (style, size) => `font-family: ${fontStyles[style]};\nfont-size: ${fontSizes[size]};`, // Backwards compatibility with `@include font(style, size)` rule
    getDesignToken,
    getDesignTokens,
    headerFontSize,
    isStyle2pt0,
    mixins: {
        ellipsis,
        ellipsis2
    },
    radius: {},
    size: (size) => fontSizes[size], // Backwards compatibility with current typography system (h0..h7)
    space: (size) => spaces[size],
    text: { ...fontStyles },
    textColor: baseStyles.textColorPrimary
};

export const theme = isStyle2pt0
    ? {
        ...waTheme,
        baseStyles: {
            ...baseStyles,
            backgroundColorPrimary: wpDesignTokens.color["palette-border-secondary"].value // L3
        },
        bodyFontSize: wpDesignTokens.bodyFontSize,
        color: {
            ...waTheme.color,
            ...wpDesignTokens.color,
            ...colorStyle2pt0
        },
        headerFontSize: wpDesignTokens.headerFontSize,
        radius: wpDesignTokens.radius
    }
    : waTheme;

export const withTheme = (Component, selectedTheme = theme) => (
    <ThemeProvider theme={selectedTheme}>{Component}</ThemeProvider>
);

if (isStyle2pt0) {
    const wrapper = document.getElementById("wrapper"); // S1
    const activeTheme = document.getElementById("theme-stylesheet"); // S2
    const body = document.body;
    if (wrapper) {
        wrapper.style.backgroundColor = theme.baseStyles.backgroundColorPrimary;
    }
    if (body) {
        body.classList.add("ui-theme");
    }
    if (activeTheme) {
        activeTheme.setAttribute("href", `/merchant/theme-new-${BUILD_TIMESTAMP}.css`); // S2 // S3
    }
}

/*

NOTES

[L1]
TODO
The optional chaining we have here is primarily to ensure that Jest does
not fail because of the error "TypeError: _store.store.getState is not a
function". Once ui-theme is more fully implemented, we will need to use
a mocked store to test that values change when the `isStyle2pt0` toggle
is enabled.

[L2]
The `wa` prefix here stands for "web application" and denotes the
internal theme we have used for some time. This differs from the the
`wp` prefix which denotes the WorldPay design tokens which we began to
implement beginning in March 2025.

[L3]
TODO
The hex value of `palette-border-secondary` is the same as the hex value
for `pallette-bg-secondary`. But we are leaving this for now because
we are also using a color that is named "secondary" in a variable that
is named "primary". Once we have the official JSON file from WorldPay,
we need to clean this up. In other words, we are leaving this as is
because it's going to change regardless.

[L4]
This keeps ESlint from throwing an error about BUILD_TIMESTAMP not being
defined.

[L5]
Beginning in August 2025, we now collect these warnings in `.walog`
which can be accessed in the browser's dev tools when working locally.

TODO
Consider adding other values we want to track and make available for
developers during debugging.

*/
